import { useEffect, useState } from 'react';
import { connectWithViewport } from 'app/hermes-redux';
import { Contact } from 'wa-storybook/components/contacts/contact-listing/contact-listing';

import { getMainHelpOptions } from 'pages/employee-assistance/employee-assistance.helpers';

import { getPhoneNumbers } from 'stores/eap-store';
import { closeModal } from 'state/modal/actions';
import type { Dispatch, AppState } from 'globals/types';
import styles from './modalities-modal.module.scss';

import { BrowserModel } from 'state/global/types';

import CareNowCard from 'react-components/modalities/cards/CareNowCard';
import ScheduleAppointmentCard from 'react-components/modalities/cards/ScheduleAppointmentCard';
import WorkLifeServiceCard from 'react-components/modalities/cards/WorkLifeServiceCard';
import ContactListingCard from 'react-components/modalities/cards/ContactListingCard';
import { ANALYTICS_CONSTANTS } from 'services/google-analytics/constants';
import { AnalyticsEvent } from 'services/google-analytics/analytics-events';
import { useLocation } from 'react-router-dom';

type Props = {
  globalPhoneNumbers: boolean;
  eapPhoneNumberLoginPage: Array<Contact>;
  dispatch: Dispatch;
  browser: BrowserModel;
  payload: {
    excludeStates?: { [key: string]: boolean };
    title?: string;
    description?: string;
    showCareNowLink?: boolean;
    modalTitle?: string;
    isLoginPage?: boolean;
  };
};

export const ModalitiesModal = ({
  globalPhoneNumbers,
  eapPhoneNumberLoginPage = [],
  browser,
  dispatch,
  payload: { showCareNowLink, modalTitle, isLoginPage },
}: Props): React.ReactNode => {
  const aboveTablet = browser.greaterThan.tablet;
  const telephoneNumbers = isLoginPage
    ? eapPhoneNumberLoginPage
    : getPhoneNumbers('employee-assistance');

  const [mounted, setMounted] = useState(false);
  const { mainOptions, contactOptions } = getMainHelpOptions();
  const location = useLocation();

  const onModalClose = () => {
    dispatch(closeModal());
  };

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    } else {
      onModalClose();
    }
  }, [location]);

  const scheduleAppointment = mainOptions['scheduleAppointment'];
  const workLifeServices = mainOptions['workLifeServices'];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          onClick={() => dispatch(closeModal())}
          className={styles['header-close-button']}
          aria-label={polyglot.t('aria.close_modal')}
        >
          <i className='icon-web_close' />
        </button>

        <h2>
          {modalTitle ||
            polyglot.t('employee_assistance.let_us_help.get_support')}
        </h2>
      </div>
      <div className={styles.content}>
        {scheduleAppointment && (
          <ScheduleAppointmentCard
            action={scheduleAppointment.action}
            analyticsEvent={scheduleAppointment.analyticsEvent}
          />
        )}

        {showCareNowLink && (
          <CareNowCard
            onClick={() => {
              dispatch(closeModal());
            }}
          />
        )}

        {workLifeServices && (
          <WorkLifeServiceCard
            href={workLifeServices.action()}
            onClick={workLifeServices.analyticsEvent}
          />
        )}

        <ContactListingCard
          phoneNumbers={telephoneNumbers}
          phoneAnalytics={() =>
            AnalyticsEvent.Life.Eap.Modalities.GetSupport.clicked(
              ANALYTICS_CONSTANTS.LIFE.EAP.MODALITIES.GETSUPPORT.CLICKED
                .ITEM_NAME.PHONE_NUMBER,
            )
          }
          withDropdown={false}
          contactOptions={contactOptions}
          isMobile={!aboveTablet}
          globalPhoneNumbers={globalPhoneNumbers}
        />
      </div>
    </div>
  );
};

ModalitiesModal.displayName = 'ModalitiesModal';

export default connectWithViewport(ModalitiesModal, (state: AppState) => {
  const {
    company: { eap_phone_number_login_page: eapPhoneNumberLoginPage },
    companySettings: { globalPhoneNumbers },
  } = state;

  return {
    eapPhoneNumberLoginPage,
    globalPhoneNumbers,
  };
});
